import { css } from '@emotion/react'
import { stripUnit } from 'polished'
import styled from '@emotion/styled'

import SectionComponent from '../Section'
import fluidRange from '../../utils/fluidRange'
import { ColorDeterminer } from '../../utils/helpers.styles'

// seeting padding-top to 0 on mobile so there's no extraneous
// gap above the billboard when the mobile image is on top of the content
export const Section = styled(SectionComponent)`
  position: relative;
  overflow: hidden;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    ${(props) =>
      props.mobileAlignImageToBottom
        ? 'padding-bottom: 0 !important;'
        : 'padding-top: 0 !important;'}
  }
`

export const Content = styled.div(
  ColorDeterminer,
  ({
    theme,
    variant,
    backgroundColor,
    alignMainContent,
    hasBackgroundImage,
    mobileAlignImageToBottom,
  }) =>
    css`
      position: relative;
      z-index: 1;
      border-radius: ${theme.components?.Billboard?.radius || 'none'};

      ${variant === 'full' &&
      !hasBackgroundImage &&
      !backgroundColor &&
      `background-color: inherit;`}
      @media (max-width: ${theme.breakpoints.sm}) {
        margin-bottom: ${mobileAlignImageToBottom ? theme.spacing.md : '0'};
      }
      @media (min-width: ${theme.breakpoints.lg}) {
        width: 50%;
        box-shadow: ${variant === 'full' ? theme.shadow : 'none'};
        ${variant === 'full' &&
        fluidRange('padding', [
          stripUnit(theme.spacing.sm),
          stripUnit(theme.spacing.lg),
        ])};
        ${variant === 'full' &&
        !backgroundColor &&
        `background-color: ${theme.colors.light.lighter.hex};`}

        ${variant === 'split' &&
        fluidRange('padding-right', [
          stripUnit(theme.spacing.lg),
          stripUnit(theme.spacing.xxl),
        ])};
      }

      text-align: ${alignMainContent === 'center' ? 'center' : 'left'};

      ${alignMainContent === 'left' &&
      `
        > .svg-image {
          justify-content: flex-start;
        }
      `}

      section:nth-of-type(even) & {
        @media (min-width: ${theme.breakpoints.lg}) {
          left: 50%;
          ${variant === 'split' &&
          `${fluidRange('padding-left', [
            stripUnit(theme.spacing.lg),
            stripUnit(theme.spacing.xxl),
          ])};
            padding-right: 0;
          `}
        }
      }
    `
)

export const Img = styled.div(
  ({ theme, variant, hasBackgroundImage }) => css`
    display: none;
    ${hasBackgroundImage && `z-index: 1;`}
    ${variant === 'split' && 'width: 50%'};

    // In decoupling from Gatsby image, i need to target the inline styles generated by gatsby on that div
    > .gatsby-image-wrapper {
      position: initial !important;

      // Gatsby Image has inline-styles with padding-bottom overflow into other sections.
      > div {
        padding-bottom: 0 !important;
      }

      picture {
        img {
          height: 101%;
        }
      }
    }

    > img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }

    @media (min-width: ${theme.breakpoints.lg}) {
      display: block;
      position: absolute !important;

      top: 0;
      right: 0;
      bottom: 0;
      left: ${variant === 'full' ? '0' : '50%'};
    }

    section:nth-of-type(even) & {
      @media (min-width: ${theme.breakpoints.lg}) {
        transform: scale(-1, 1);
        right: ${variant === 'full' ? '0' : '50%'};
        left: 0;
      }
    }
  `
)

export const MobileImg = styled.div(
  // per the above comment had to update spacing to make
  // content even when there is no mobile image or mobile image is on top
  ({ theme, mobileAlignImageToBottom, hasBackgroundImage }) => css`
    margin-bottom: ${!mobileAlignImageToBottom ? theme.spacing.lg : '0'};
    margin-left: -32px;
    margin-right: -32px;
    ${hasBackgroundImage &&
    `
      z-index: 1;
      position: relative;
    `}
    @media (min-width: ${theme.breakpoints.sm}) {
      display: none;
    }
  `
)

export const BGImage = styled.div`
  display: block;
  position: absolute !important;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  // In decoupling from Gatsby image, i need to target the inline styles generated by gatsby on that div
  > .gatsby-image-wrapper {
    position: initial !important;

    // Gatsby Image has inline-styles with padding-bottom overflow into other sections.
    > div {
      padding-bottom: 0 !important;
    }

    picture {
      img {
        height: 100%;
      }
    }
  }

  > img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`

export const BackgroundImage = styled(BGImage)`
  ${(props) =>
    props.mobileBackgroundImage &&
    `
    @media (max-width: 939px) {
      display: none;
    }
  `}
`

export const MobileBackgroundImage = styled(BGImage)`
  @media (min-width: ${(props) => props.theme.breakpoints.lg}) {
    display: none;
  }
`

export const RelativeContent = styled.div`
  position: relative;
`
